// Client-side media pipeline: compress (images) → get a signed URL → PUT the
// bytes straight to GCS → register the Media doc with the API.
import axios from 'axios'
import JSZip from 'jszip'
import imageCompression from 'browser-image-compression'
import { signUpload, createMedia, signMemberUpload, createMemberMedia } from './api'

const MAX_VIDEO_MB = Number(process.env.REACT_APP_MAX_VIDEO_MB || 200)

// Read an image's pixel dimensions from a Blob/File.
function imageDimensions(blob) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(url)
    }
    img.onerror = () => {
      resolve({ width: 0, height: 0 })
      URL.revokeObjectURL(url)
    }
    img.src = url
  })
}

// Compress an image File to a web-friendly size; returns { blob, width, height }.
async function compressImage(file) {
  const blob = await imageCompression(file, {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 2000,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.82,
  })
  const { width, height } = await imageDimensions(blob)
  return { blob, width, height }
}

// Upload one File (image or video) for an event. onProgress(0..1) is optional.
// Returns the created Media doc.
export async function uploadMedia({ groupId, eventId, file, caption = '', postedBy = '', onProgress }) {
  const isVideo = file.type.startsWith('video/')
  const isImage = file.type.startsWith('image/')
  if (!isVideo && !isImage) throw new Error('Only image and video files are supported.')

  let blob = file
  let width = 0
  let height = 0
  let contentType = file.type

  if (isImage) {
    const compressed = await compressImage(file)
    blob = compressed.blob
    width = compressed.width
    height = compressed.height
    contentType = 'image/jpeg'
  } else {
    // No in-browser transcode; just guard against oversized videos.
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      throw new Error(`Video is larger than ${MAX_VIDEO_MB}MB. Please compress it before uploading.`)
    }
  }

  // 1) Ask the API for a signed PUT URL.
  const { uploadUrl, gcsKey, publicUrl } = await signUpload(groupId, {
    filename: file.name,
    contentType,
  })

  // 2) PUT the bytes directly to GCS (plain axios — no auth header, no baseURL).
  await axios.put(uploadUrl, blob, {
    headers: { 'Content-Type': contentType },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(e.loaded / e.total)
    },
  })

  // 3) Register the Media doc.
  return createMedia(groupId, eventId, {
    type: isVideo ? 'video' : 'image',
    gcsKey,
    url: publicUrl,
    caption,
    postedBy,
    width,
    height,
    sizeBytes: blob.size,
  })
}

// ── Downloads ─────────────────────────────────────────────────────────────────
// Media lives on GCS (a different origin), so the <a download> attribute is
// ignored — we must fetch the bytes as a blob to force a real save. This needs
// the bucket CORS to allow GET from the site origin (see README CORS setup).

const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i

// A friendly filename for a media item, recovered from its GCS key
// (`groups/<id>/<uuid>-<originalname>` → `<originalname>`).
export function filenameForMedia(m) {
  try {
    const path = m.gcsKey || new URL(m.url).pathname
    const base = decodeURIComponent(String(path).split('/').pop() || '')
    const name = base.replace(UUID_PREFIX, '')
    return name || base || (m.type === 'video' ? 'video.mp4' : 'image.jpg')
  } catch {
    return m.type === 'video' ? 'video.mp4' : 'image.jpg'
  }
}

function triggerDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename || 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

// Download a single media item to the user's device.
export async function downloadMedia(m) {
  const res = await fetch(m.url, { mode: 'cors' })
  if (!res.ok) throw new Error('Could not fetch the file.')
  triggerDownload(await res.blob(), filenameForMedia(m))
}

// Zip up many media items into one archive and download it. `pathFn(m, i)`
// chooses each file's path inside the zip (default: numbered, flat). Files that
// fail to fetch are skipped. onProgress(0..1) fires as each file is fetched.
export async function downloadAll(mediaList, zipName, { pathFn, onProgress } = {}) {
  const zip = new JSZip()
  const used = new Set()
  let added = 0

  for (let i = 0; i < mediaList.length; i++) {
    const m = mediaList[i]
    try {
      const res = await fetch(m.url, { mode: 'cors' })
      if (!res.ok) throw new Error('fetch failed')
      const blob = await res.blob()
      const wanted = pathFn ? pathFn(m, i) : `${String(i + 1).padStart(3, '0')}-${filenameForMedia(m)}`
      let path = wanted
      let n = 1
      while (used.has(path)) {
        const dot = wanted.lastIndexOf('.')
        path = dot > 0 ? `${wanted.slice(0, dot)}-${n}${wanted.slice(dot)}` : `${wanted}-${n}`
        n++
      }
      used.add(path)
      zip.file(path, blob)
      added++
    } catch {
      // skip unreachable files; keep going
    }
    onProgress && onProgress((i + 1) / mediaList.length)
  }

  if (!added) throw new Error('None of the files could be downloaded.')
  const content = await zip.generateAsync({ type: 'blob' })
  triggerDownload(content, zipName || 'media.zip')
}

// Upload one image (or admin video) as a trip member (or admin), into a day.
// Uses the member endpoints (slug + dayId). The server flags images from
// non-admins as `uncompressed`. Videos are only accepted for admins.
export async function uploadMemberMedia({ slug, dayId, file, onProgress }) {
  const isVideo = file.type.startsWith('video/')
  const isImage = file.type.startsWith('image/')
  if (!isImage && !isVideo) throw new Error('Only image and video files are supported.')

  let blob = file
  let width = 0
  let height = 0
  let contentType = file.type

  if (isImage) {
    const compressed = await compressImage(file)
    blob = compressed.blob
    width = compressed.width
    height = compressed.height
    contentType = 'image/jpeg'
  } else {
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      throw new Error(`Video is larger than ${MAX_VIDEO_MB}MB. Please compress it before uploading.`)
    }
  }

  const { uploadUrl, gcsKey, publicUrl } = await signMemberUpload(slug, dayId, { filename: file.name, contentType })
  await axios.put(uploadUrl, blob, {
    headers: { 'Content-Type': contentType },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(e.loaded / e.total)
    },
  })

  return createMemberMedia(slug, dayId, {
    type: isVideo ? 'video' : 'image',
    gcsKey,
    url: publicUrl,
    width,
    height,
    sizeBytes: blob.size,
  })
}

// Upload a group cover image. Returns { gcsKey, url }.
export async function uploadCover({ groupId, file }) {
  const { blob } = await compressImage(file)
  const { uploadUrl, gcsKey, publicUrl } = await signUpload(groupId, {
    filename: `cover-${file.name}`,
    contentType: 'image/jpeg',
  })
  await axios.put(uploadUrl, blob, { headers: { 'Content-Type': 'image/jpeg' } })
  return { gcsKey, url: publicUrl }
}
